import { useCallback, useRef, useState } from "react";

const TIMER_THRESHOLDS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 600, label: "10 minutes remaining" },
  { seconds: 300, label: "5 minutes remaining" },
  { seconds: 120, label: "2 minutes remaining" },
  { seconds: 60, label: "1 minute remaining" },
  { seconds: 30, label: "30 seconds remaining" },
  { seconds: 10, label: "10 seconds remaining" },
];

export function useTimerAnnouncements() {
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [isAssertive, setIsAssertive] = useState(false);
  const announcedThresholds = useRef<Set<number>>(new Set());
  const prevSecondsRef = useRef<number | null>(null);

  const checkAnnouncement = useCallback((remainingSeconds: number) => {
    if (remainingSeconds < 0) return;
    const prev = prevSecondsRef.current;
    prevSecondsRef.current = remainingSeconds;

    if (prev === null) return; // First call — just record, don't announce

    for (const threshold of TIMER_THRESHOLDS) {
      if (
        prev > threshold.seconds &&
        remainingSeconds <= threshold.seconds &&
        !announcedThresholds.current.has(threshold.seconds)
      ) {
        announcedThresholds.current.add(threshold.seconds);
        setAnnouncement(threshold.label);
        setIsAssertive(threshold.seconds <= 60);
        break;
      }
    }
  }, []);

  const resetAnnouncements = useCallback(() => {
    announcedThresholds.current.clear();
    prevSecondsRef.current = null;
    setAnnouncement(null);
    setIsAssertive(false);
  }, []);

  return { announcement, isAssertive, checkAnnouncement, resetAnnouncements };
}

export function parseDisplayTimeToSeconds(displayTime: string | null): number {
  if (!displayTime || displayTime === "--:--") return -1;
  const parts = displayTime.split(":");
  if (parts.length !== 2) return -1;
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return -1;
  return minutes * 60 + seconds;
}
